import { PkCard } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useCardOverlay } from '../../hooks/useCardOverlay';
import type { CardLayout } from '../../types';
import { CardFormat } from '../../types';
import { trackVirtPath } from '../../utils/tracking';
import { getLink } from '../../utils/utility';
import { VideoIcon } from '../VideoIcon';

import CardOverlay from './CardOverlay';

import { trackCardElement } from '@services/GoogleTagManagerService';
import PictureHelper from '@shared/PictureHelper';
import type { UefatvEditorialContent } from '@shared-types/uefatv.types';
import { AccessLevel } from '@shared-types/uefatv.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

dayjs.extend(duration);

import * as styles from './styles.module.scss';

const formatMap = {
  [CardFormat.WIDE]: ['0@w1'],
  [CardFormat.SQUARE]: ['0@s1'],
  [CardFormat.PORTRAIT]: ['0@p2'],
};

export const Card = ({
  item,
  layout = { cardFormat: 'wide', orientation: 'portrait', hideDescription: false },
  grouped = false,
  bucketId = '',
}: {
  bucketId?: string;
  item: UefatvEditorialContent;
  layout?: CardLayout;
  grouped?: boolean;
}) => {
  const navigate = useNavigate();
  const { handleMouseEnter, handleMouseLeave, showOverlay } = useCardOverlay(grouped);

  const handleClick = (e: any) => {
    const data = {
      id: item.id,
      name: item.title,
      category: 'video card',
      variant: item.accessLevel === AccessLevel.GRANTED.toString() ? 'unlocked' : 'locked',
    };
    GoogleTagManagerHelper.gtmCardElementClick(e?.target as HTMLElement, data);
    trackVirtPath(item.id, item.type) && navigate(getLink(item, bucketId));
  };

  const imageFormats = useMemo(() => {
    return formatMap[layout?.cardFormat] || ['0@w1'];
  }, [layout?.cardFormat]);

  const trackingData = {
    id: item?.id,
    name: item?.title,
    category: 'video card',
  };
  const noPortraitImg = useMemo(() => {
    return (
      layout.cardFormat === CardFormat.PORTRAIT &&
      item?.mainMedia?.image?.portrait?.p1 === item?.mainMedia?.image?.wide?.w1
    );
  }, [layout.cardFormat, item?.mainMedia?.image?.portrait?.p1, item?.mainMedia?.image?.wide?.w1]);

  if (!item) {
    return null;
  }
  return (
    <PkCard
      ref={trackCardElement(trackingData, true)}
      aria-label={item.title}
      role='article'
      className={clsx({ [styles.card]: true, [styles.grouped]: grouped })}
      style={{ cursor: 'pointer' }}
      content={layout?.content}
      orientation={layout.orientation}
      key={item.id}
      transparency={layout?.transparency}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div slot='heading'>
        <CardOverlay item={item} isHovered={showOverlay} />
        {noPortraitImg && (
          <div
            className={styles.bgImage}
            style={
              {
                backgroundImage: noPortraitImg && `url(${item?.mainMedia?.image?.wide?.w1})`,
              } as CSSProperties
            }
          />
        )}
        <VideoIcon item={item} grouped={grouped} />
        <div className={clsx({ [styles.imgContainer]: noPortraitImg, [styles.zoomImg]: !showOverlay })}>
          {item.mainMedia?.image && (
            <PictureHelper image={item.mainMedia?.image} formats={imageFormats} title={item.title} className='img' />
          )}
        </div>
      </div>
      {!layout?.hideDescription && (
        <div slot='body'>
          <p
            className={clsx('pk-m--0 pk-font--base-semibold pk-text--text-04 pk-line-height--md', {
              [' pk-font-size--s pk-font-size-md--m']: grouped,
              [styles.clamped]: grouped,
              [' pk-font-size--m pk-font-size-md--l']: !grouped,
            })}
          >
            {item.title}
          </p>
        </div>
      )}
    </PkCard>
  );
};
